/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useMemo, useState } from "react";
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DeviceDetailsProps, AlarmDefinition } from "../types";
import AlarmTable from "../components/AlarmTable";
import DetailRow from "../components/DetailRow";
import DasPowerDetectorChart from "./DasPowerDetectorChart";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { isBitSet, getAlarmLedColor } from "../utils";
import {
  buildPowerDetectorParams,
  createPowerDetectorChartConfig,
  PowerDetectorParams,
} from "./dasChartUtils";

const DxruDeviceDetails: React.FC<DeviceDetailsProps> = ({
  device,
  parsedAttributes,
  statusLabel,
  alarmsOnly = false,
  siteObjectKey,
}) => {
  const isDisconnected = statusLabel === "Disconnected";

  const normalizeAttrKey = (value: string) => value.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  const getAttrValueInsensitive = (key: string) => {
    const direct = parsedAttributes[key];
    if (direct !== undefined) return direct;

    const lowerKey = key.toLowerCase();
    const matchedKey = Object.keys(parsedAttributes).find((k) => k.toLowerCase() === lowerKey);
    if (matchedKey) return parsedAttributes[matchedKey];

    const normalizedKey = normalizeAttrKey(key);
    const matchedNormalizedKey = Object.keys(parsedAttributes).find(
      (k) => normalizeAttrKey(k) === normalizedKey
    );
    return matchedNormalizedKey ? parsedAttributes[matchedNormalizedKey] : undefined;
  };

  const getDxruValue = (suffix: string) => {
    const preferred = getAttrValueInsensitive(`dxRU${suffix}`);
    if (preferred !== undefined) return preferred;

    // Defensive fallback if backend sends 10G keys for legacy data
    return getAttrValueInsensitive(`dxRU10G${suffix}`);
  };

  const getDxruPortValue = (suffix: string, idx: number) => getDxruValue(`${suffix}${idx}`);
  const getDxruSfpValue = (suffix: string, idx: number) => getDxruValue(`${suffix}-${idx}`);

  const dxruAlarms = getDxruValue("Alarms");
  const dxruAlarmsMask = getDxruValue("AlarmsMask");
  const hasDxruAlarms = dxruAlarms !== undefined || dxruAlarmsMask !== undefined;

  const dxruConnectDeuPortNum = getDxruValue("ConnectDeuPortNum");
  const dxruEquipmentModel = getDxruValue("EquipmentModel") || device.model;
  const dxruEquipmentSn = getDxruValue("EquipmentSN") || device.serialNumber;
  const dxruSiteInfo = getDxruValue("SiteInfo");
  const dxruTemperature = getDxruValue("Temperature");
  const dxruFirmwareVersion = getDxruValue("Firmwareversion");
  const dxruHardwareVersion = getDxruValue("Hardwareversion");
  const dxruDcVoltage = getDxruValue("DCVoltage");
  const dxruPowerConsumption = getDxruValue("PowerConsumption");
  const hasDeviceInfo =
    dxruConnectDeuPortNum ||
    dxruEquipmentModel ||
    dxruEquipmentSn ||
    dxruSiteInfo ||
    dxruTemperature !== undefined ||
    dxruFirmwareVersion ||
    dxruHardwareVersion ||
    dxruDcVoltage !== undefined ||
    dxruPowerConsumption !== undefined;

  const getArrayAttr = (keys: string[]) => {
    for (const key of keys) {
      const value = getAttrValueInsensitive(key);
      if (Array.isArray(value)) {
        return value;
      }
    }
    return [] as any[];
  };

  const dxruFanAlarm = getArrayAttr(["dxRUFanAlram", "dxRUFanAlarm"]);
  const dxruVswrAlarm = getArrayAttr(["dxRUVswrAlram", "dxRUVswrAlarm"]);
  const dxruAntennaAlarm = getArrayAttr(["dxRUAntennaAlram", "dxRUAntennaAlarm"]);
  const dxruPaAlarm = getArrayAttr(["dxRUPaAlram", "dxRUPaAlarm"]);

  const sortByNumber = (items: any[], key: string) =>
    [...items].sort((a, b) => Number(a?.[key]) - Number(b?.[key]));

  const dxruFanAlarmSorted = sortByNumber(dxruFanAlarm, "dxRuFanNumber");
  const dxruVswrAlarmSorted = sortByNumber(dxruVswrAlarm, "dxRuVswrNumber");
  const dxruAntennaAlarmSorted = sortByNumber(dxruAntennaAlarm, "dxRuAntennaNumber");
  const dxruPaAlarmSorted = sortByNumber(dxruPaAlarm, "dxRuPaNumber");

  const hasFanAlarm = dxruFanAlarmSorted.length > 0;
  const hasVswrAlarm = dxruVswrAlarmSorted.length > 0;
  const hasAntennaAlarm = dxruAntennaAlarmSorted.length > 0;
  const hasPaAlarm = dxruPaAlarmSorted.length > 0;

  const dxruSwitchColumns = [1, 2, 3, 4];
  const hasDxruRfInfo = dxruSwitchColumns.some((idx) => (
    getDxruPortValue("RFSwitch", idx) !== undefined ||
    getDxruPortValue("DlPwrOut", idx) !== undefined ||
    getDxruPortValue("UlPwrIn", idx) !== undefined
  ));

  const dxruSfpColumns = [1, 2];
  const hasDxruSfpInfo = dxruSfpColumns.some((idx) => (
    getDxruSfpValue("SFPAlarms", idx) !== undefined ||
    getDxruSfpValue("SFPAlarmsMask", idx) !== undefined
  ));

  const dxruSfpAlarmDefinitions: AlarmDefinition[] = [
    { bit: 1, name: "Cpri Link Rx Alarm" },
    { bit: 2, name: "Cpri Link Tx Alarm" },
    { bit: 3, name: "Cpri Sync Alarm" },
    { bit: 4, name: "Cpri Temperature Alarm" },
    { bit: 5, name: "Cpri Manufacturer Invalid Alarm" },
    { bit: 6, name: "Cpri Rate Mismatch Alarm" }
  ];

  const dxruAlarmDefinitions: AlarmDefinition[] = [
    { bit: 1, name: "Overall Alarm" },
    { bit: 2, name: "Hardware Fault Alarm" },
    { bit: 3, name: "Over Temperature Alarm" },
    { bit: 4, name: "Firmware Mismatch Alarm" },
    { bit: 5, name: "Overflow Alarm" },
    { bit: 6, name: "Low Transmission Alarm" },
    { bit: 7, name: "DC Voltage Lower Alarm" },
    { bit: 8, name: "Over Power Consumption Alarm" },
  ];

  const formatDxruSwitch = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    return String(value) === "1" ? "On" : String(value) === "0" ? "Off" : String(value);
  };

  const formatDxruDbm = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return "N/A";
    return `${num.toFixed(2)} dBm`;
  };

  const formatDxruSfpDbm = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return "N/A";
    return num.toFixed(2);
  };

  const formatDxruSfpMetric = (value: unknown): string => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return "N/A";
    return String(num);
  };

  const renderDxruMetricValueWithUnit = (
    value: unknown,
    unit: string,
    formatter: (v: unknown) => string = formatDxruSfpMetric
  ) => {
    const formattedValue = formatter(value);

    if (formattedValue === "N/A") {
      return <span className="font-medium text-muted-foreground">N/A</span>;
    }

    return <span className="font-medium">{formattedValue} {unit}</span>;
  };

  const remoteEquipmentObjectKey = useMemo(() => {
    return (
      device.objectKey ||
      parsedAttributes.objectKey ||
      parsedAttributes.manageObject ||
      parsedAttributes.managedObject ||
      parsedAttributes.objectId ||
      "DXRU-1"
    );
  }, [
    device.objectKey,
    parsedAttributes.objectKey,
    parsedAttributes.manageObject,
    parsedAttributes.managedObject,
    parsedAttributes.objectId,
  ]);

  const serviceDeviceObjectKey =
    siteObjectKey || device.servicesDeviceObjectKey || device.serialNumber || "";

  const dlPowerDetectorIndicators = [
    "dxRUDlPwrOut1",
    "dxRUDlPwrOut2",
    "dxRUDlPwrOut3",
    "dxRUDlPwrOut4",
  ];

  const ulPowerDetectorIndicators = [
    "dxRUUlPwrIn1",
    "dxRUUlPwrIn2",
    "dxRUUlPwrIn3",
    "dxRUUlPwrIn4",
  ];

  const hasDlPowerDetectorChart = [1, 2, 3, 4].some(
    (idx) => getDxruPortValue("DlPwrOut", idx) !== undefined
  );

  const hasUlPowerDetectorChart = [1, 2, 3, 4].some(
    (idx) => getDxruPortValue("UlPwrIn", idx) !== undefined
  );

  const hasPowerDetectorChart = hasDlPowerDetectorChart || hasUlPowerDetectorChart;

  const dlPowerDetectorBandNames = [
    "DL Port 1",
    "DL Port 2",
    "DL Port 3",
    "DL Port 4",
  ];

  const ulPowerDetectorBandNames = [
    "UL Port 1",
    "UL Port 2",
    "UL Port 3",
    "UL Port 4",
  ];

  const powerDetectorBandNumbers = [1, 2, 3, 4];

  const dlPowerDetectorChartParams = useMemo<PowerDetectorParams | null>(() => {
    if (!hasDlPowerDetectorChart) return null;

    return {
      remoteEquipmentObjectKey,
      indicators: dlPowerDetectorIndicators,
      bandNames: dlPowerDetectorBandNames,
      bandNumbers: powerDetectorBandNumbers,
      yAxisMin: -600,
      yAxisMax: 100,
    };
  }, [hasDlPowerDetectorChart, remoteEquipmentObjectKey]);

  const ulPowerDetectorChartParams = useMemo<PowerDetectorParams | null>(() => {
    if (!hasUlPowerDetectorChart) return null;

    return {
      remoteEquipmentObjectKey,
      indicators: ulPowerDetectorIndicators,
      bandNames: ulPowerDetectorBandNames,
      bandNumbers: powerDetectorBandNumbers,
      yAxisMin: -600,
      yAxisMax: 100,
    };
  }, [hasUlPowerDetectorChart, remoteEquipmentObjectKey]);

  const dlPowerDetectorChartConfig = useMemo(
    () =>
      dlPowerDetectorChartParams
        ? createPowerDetectorChartConfig("dl", dlPowerDetectorChartParams)
        : null,
    [dlPowerDetectorChartParams]
  );

  const ulPowerDetectorChartConfig = useMemo(
    () =>
      ulPowerDetectorChartParams
        ? createPowerDetectorChartConfig("ul", ulPowerDetectorChartParams)
        : null,
    [ulPowerDetectorChartParams]
  );

  const dlPowerDetectorAdditionalParams = useMemo(
    () =>
      dlPowerDetectorChartParams
        ? {
            ...buildPowerDetectorParams(
              serviceDeviceObjectKey,
              remoteEquipmentObjectKey,
              dlPowerDetectorIndicators
            ),
          }
        : null,
    [dlPowerDetectorChartParams, serviceDeviceObjectKey, remoteEquipmentObjectKey]
  );

  const ulPowerDetectorAdditionalParams = useMemo(
    () =>
      ulPowerDetectorChartParams
        ? {
            ...buildPowerDetectorParams(
              serviceDeviceObjectKey,
              remoteEquipmentObjectKey,
              ulPowerDetectorIndicators
            ),
          }
        : null,
    [ulPowerDetectorChartParams, serviceDeviceObjectKey, remoteEquipmentObjectKey]
  );

  const [fullscreenChartType, setFullscreenChartType] = useState<"dl" | "ul" | null>(null);

  const renderStatusAlarmCard = (
    title: string,
    items: any[],
    numberKey: string,
    alarmKey: string,
    maskKey: string,
    labelPrefix: string
  ) => {
    if (items.length === 0) return null;

    return (
      <Card className="mb-2 overflow-hidden">
        <CardHeader className="pb-3 px-4 pt-4">
          <CardTitle className="text-sm">{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {items.map((item: any, index: number) => {
              const itemNumber = item?.[numberKey] ?? index + 1;
              const isAlarmActive = Number(item?.[alarmKey]) > 0;
              const isMaskEnabled = Number(item?.[maskKey]) > 0;

              return (
                <div
                  key={`${title}-${itemNumber}-${index}`}
                  className="rounded-xl border p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">
                      {labelPrefix} {itemNumber}
                    </div>

                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: getAlarmLedColor(isDisconnected, isAlarmActive),
                      }}
                    />
                  </div>

                  <div className="mt-3 text-xs flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">
                      Status:{" "}
                      <span className="font-medium text-foreground">
                        {isAlarmActive ? "Alarm Active" : "Normal"}
                      </span>
                    </span>
                    <Checkbox
                      checked={isMaskEnabled}
                      disabled
                      size="small"
                      sx={{
                        padding: 0,
                        transform: "scale(0.7)",
                        color: "var(--foreground)",
                        "&.Mui-disabled": {
                          color: "var(--muted-foreground)",
                        },
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDxruSfpStatusCard = () => {
    if (!hasDxruSfpInfo) return null;

    return (
      <Card className="shadow-sm border mb-2">
        <CardHeader className="px-4 py-3 border-b">
          <CardTitle className="text-sm font-semibold tracking-tight">
            SFP Status
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {dxruSfpColumns.map((idx) => {
              const alarmsVal = getDxruSfpValue("SFPAlarms", idx);
              const maskVal = getDxruSfpValue("SFPAlarmsMask", idx);

              return (
                <div
                  key={`dxru-sfp-status-${idx}`}
                  className="rounded-xl border bg-card p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm font-semibold">
                      Port {idx}
                    </div>

                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: getAlarmLedColor(
                          isDisconnected,
                          Number(alarmsVal) > 0
                        ),
                      }}
                    />
                  </div>

                  <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        Temperature
                      </span>

                      {renderDxruMetricValueWithUnit(
                        getDxruSfpValue("SFPTemperature", idx),
                        "°C"
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        TX Power
                      </span>

                      {renderDxruMetricValueWithUnit(
                        getDxruSfpValue("SFPTxPower", idx),
                        "dBm",
                        formatDxruSfpDbm
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        RX Power
                      </span>

                      {renderDxruMetricValueWithUnit(
                        getDxruSfpValue("SFPRxPower", idx),
                        "dBm",
                        formatDxruSfpDbm
                      )}
                    </div>
                  </div>

                  <div className="mt-4 border-t pt-3 space-y-2">
                    {dxruSfpAlarmDefinitions.map((alarmDef) => {
                      const isAlarmActive = isBitSet(alarmsVal, alarmDef.bit);
                      const isMaskEnabled = isBitSet(maskVal, alarmDef.bit);

                      return (
                        <div
                          key={`dxru-sfp-status-${idx}-${alarmDef.bit}`}
                          className="flex items-center justify-between gap-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor: getAlarmLedColor(
                                  isDisconnected,
                                  isAlarmActive
                                ),
                              }}
                            />

                            <span className="text-[11px] truncate text-muted-foreground">
                              {alarmDef.name}
                            </span>
                          </div>

                          <Checkbox
                            checked={isMaskEnabled}
                            disabled
                            size="small"
                            sx={{
                              padding: 0,
                              transform: "scale(0.7)",
                              color: "var(--foreground)",
                              "&.Mui-disabled": {
                                color: "var(--muted-foreground)",
                              },
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (alarmsOnly) {
    if (!hasDxruSfpInfo && !hasDxruAlarms) return null;

    return (
      <>
        {renderDxruSfpStatusCard()}

        {hasDxruAlarms && (
          <Card className="mb-2 overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4">
              <CardTitle className="text-sm">DxRU Alarms</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlarmTable
                alarms={dxruAlarms}
                alarmsMask={dxruAlarmsMask}
                alarmDefinitions={dxruAlarmDefinitions}
                isDisconnected={isDisconnected}
              />
            </CardContent>
          </Card>
        )}
      </>
    );
  }

  if (
    !hasDeviceInfo &&
    !hasDxruAlarms &&
    !hasDxruRfInfo &&
    !hasDxruSfpInfo &&
    !hasPowerDetectorChart &&
    !hasFanAlarm &&
    !hasVswrAlarm &&
    !hasAntennaAlarm &&
    !hasPaAlarm
  ) {
    return null;
  }

  return (
    <>
      {hasDeviceInfo && (
        <Card className="mb-2">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">Device Information</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            {dxruConnectDeuPortNum && <DetailRow label="Connected DEU Port" value={dxruConnectDeuPortNum} />}
            {dxruSiteInfo && <DetailRow label="Site" value={dxruSiteInfo} />}
            {dxruEquipmentModel && <DetailRow label="Model" value={dxruEquipmentModel} />}
            {dxruEquipmentSn && <DetailRow label="Serial Number" value={dxruEquipmentSn} />}
            {dxruTemperature !== undefined && <DetailRow label="Temperature" value={`${dxruTemperature}°C`} />}
            {dxruDcVoltage !== undefined && <DetailRow label="DC Voltage" value={`${dxruDcVoltage}`} />}
            {dxruPowerConsumption !== undefined && <DetailRow label="Power Consumption" value={`${dxruPowerConsumption}`} />}
            {dxruFirmwareVersion && <DetailRow label="Firmware Version" value={dxruFirmwareVersion} />}
            {dxruHardwareVersion && <DetailRow label="Hardware Version" value={dxruHardwareVersion} />}
          </CardContent>
        </Card>
      )}

      {hasDxruRfInfo && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">RF Information</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Type</TableHead>
                  {dxruSwitchColumns.map((idx) => (
                    <TableHead key={`hdr-${idx}`} className="text-left min-w-[110px]">
                      Port {idx}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>Switch</TableCell>
                  {dxruSwitchColumns.map((idx) => (
                    <TableCell key={`switch-${idx}`} className="text-left">
                      {formatDxruSwitch(getDxruPortValue("RFSwitch", idx))}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell>Downlink Power</TableCell>
                  {dxruSwitchColumns.map((idx) => (
                    <TableCell key={`dl-${idx}`} className="text-left">
                      {formatDxruDbm(getDxruPortValue("DlPwrOut", idx))}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell>Uplink Power</TableCell>
                  {dxruSwitchColumns.map((idx) => (
                    <TableCell key={`ul-${idx}`} className="text-left">
                      {formatDxruDbm(getDxruPortValue("UlPwrIn", idx))}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {dlPowerDetectorChartConfig && dlPowerDetectorAdditionalParams && (
        <Card className="mb-2">
          <CardContent className="px-4 pb-4 pt-0">
            <div style={{ height: 300, width: "100%" }}>
              <DasPowerDetectorChart
                config={{
                  ...dlPowerDetectorChartConfig,
                  title: "DL Power Detector",
                  csvFilename: "dxru_dl_power_detector",
                  chartImageFilename: "dxru_dl_power_detector_chart",
                }}
                onFullscreenToggle={() => setFullscreenChartType("dl")}
                isFullscreen={false}
                additionalParams={dlPowerDetectorAdditionalParams}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {ulPowerDetectorChartConfig && ulPowerDetectorAdditionalParams && (
        <Card className="mb-2">
          <CardContent className="px-4 pb-4 pt-0">
            <div style={{ height: 300, width: "100%" }}>
              <DasPowerDetectorChart
                config={{
                  ...ulPowerDetectorChartConfig,
                  title: "UL Power Detector",
                  csvFilename: "dxru_ul_power_detector",
                  chartImageFilename: "dxru_ul_power_detector_chart",
                }}
                onFullscreenToggle={() => setFullscreenChartType("ul")}
                isFullscreen={false}
                additionalParams={ulPowerDetectorAdditionalParams}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {renderDxruSfpStatusCard()}

      {hasDxruAlarms && (
        <Card className="mb-2 overflow-hidden">
          <CardHeader className="pb-3 px-4 pt-4">
            <CardTitle className="text-sm">DxRU Alarms</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <AlarmTable
              alarms={dxruAlarms}
              alarmsMask={dxruAlarmsMask}
              alarmDefinitions={dxruAlarmDefinitions}
              isDisconnected={isDisconnected}
            />
          </CardContent>
        </Card>
      )}

      {renderStatusAlarmCard(
        "Fan Alarm",
        dxruFanAlarmSorted,
        "dxRuFanNumber",
        "dxRuFanAlarm",
        "dxRuFanAlarmMask",
        "Fan"
      )}

      {renderStatusAlarmCard(
        "VSWR Alarm",
        dxruVswrAlarmSorted,
        "dxRuVswrNumber",
        "dxRuVswrAlarm",
        "dxRuVswrAlarmMask",
        "VSWR"
      )}

      {renderStatusAlarmCard(
        "Antenna Alarm",
        dxruAntennaAlarmSorted,
        "dxRuAntennaNumber",
        "dxRuAntennaAlarm",
        "dxRuAntennaAlarmMask",
        "Antenna"
      )}

      {renderStatusAlarmCard(
        "PA Alarm",
        dxruPaAlarmSorted,
        "dxRuPaNumber",
        "dxRuPaAlarm",
        "dxRuPaAlarmMask",
        "PA"
      )}

      <Dialog
        open={fullscreenChartType !== null}
        onClose={() => setFullscreenChartType(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "var(--card) !important",
            border: "1px solid var(--border)",
            borderRadius: "12px",
            minHeight: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid var(--border)",
            pb: 2,
            px: 4,
          }}
        >
          <IconButton onClick={() => setFullscreenChartType(null)} size="small" sx={{ color: "var(--foreground)" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
          {fullscreenChartType === "dl" && dlPowerDetectorChartConfig && dlPowerDetectorAdditionalParams && (
            <DasPowerDetectorChart
              config={{
                ...dlPowerDetectorChartConfig,
                title: "",
                csvFilename: "dxru_dl_power_detector",
                chartImageFilename: "dxru_dl_power_detector_chart",
              }}
              onFullscreenToggle={() => setFullscreenChartType(null)}
              isFullscreen={true}
              additionalParams={dlPowerDetectorAdditionalParams}
            />
          )}

          {fullscreenChartType === "ul" && ulPowerDetectorChartConfig && ulPowerDetectorAdditionalParams && (
            <DasPowerDetectorChart
              config={{
                ...ulPowerDetectorChartConfig,
                title: "",
                csvFilename: "dxru_ul_power_detector",
                chartImageFilename: "dxru_ul_power_detector_chart",
              }}
              onFullscreenToggle={() => setFullscreenChartType(null)}
              isFullscreen={true}
              additionalParams={ulPowerDetectorAdditionalParams}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DxruDeviceDetails;
